import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { AdminCropGestureViewport } from '@/components/admin/AdminCropGestureViewport';
import {
  ADMIN_CROP_PRESET_ORDER,
  ADMIN_CROP_PRESETS,
  DEFAULT_CROP_TRANSFORM,
  buildCroppedImageFile,
  resolveCropAspect,
  type AdminCropPresetId,
  type AdminCropTransform,
} from '@/lib/adminImageCrop';

type Props = {
  open: boolean;
  source: string | null;
  fileName: string;
  title?: string;
  defaultPreset?: AdminCropPresetId;
  allowedPresets?: AdminCropPresetId[];
  saving?: boolean;
  onConfirm: (file: File, preset: AdminCropPresetId) => void | Promise<void>;
  onConfirmRaw?: (file: File) => void | Promise<void>;
  onCancel: () => void;
};

export function AdminImageCropModal({
  open,
  source,
  fileName,
  title = 'Кадрирование фото',
  defaultPreset = 'catalog_wide',
  allowedPresets = ADMIN_CROP_PRESET_ORDER,
  saving = false,
  onConfirm,
  onConfirmRaw,
  onCancel,
}: Props) {
  const presets = useMemo(
    () => allowedPresets.map((id) => ADMIN_CROP_PRESETS[id]),
    [allowedPresets],
  );
  const [presetId, setPresetId] = useState<AdminCropPresetId>(defaultPreset);
  const [transform, setTransform] = useState<AdminCropTransform>(DEFAULT_CROP_TRANSFORM);
  const [naturalAspect, setNaturalAspect] = useState(16 / 9);

  useEffect(() => {
    if (!open) return;
    setPresetId(defaultPreset);
    setTransform(DEFAULT_CROP_TRANSFORM);
  }, [open, source, defaultPreset]);

  useEffect(() => {
    if (!open || !source) return;
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth || 1;
      const h = img.naturalHeight || 1;
      setNaturalAspect(w / h);
    };
    img.src = source;
  }, [open, source]);

  if (!open || !source) return null;

  const preset = ADMIN_CROP_PRESETS[presetId];
  const cropAspect = preset.naturalAspect ? naturalAspect : preset.aspect;
  const aspectCss = `${cropAspect} / 1`;
  const imgStyle = {
    transform: `translate(${transform.panX}%, ${transform.panY}%) scale(${transform.zoom})`,
    transformOrigin: 'center center' as const,
  };

  const handleSave = async () => {
    const cropped = await buildCroppedImageFile(source, fileName, preset, transform);
    if (!cropped) return;
    await onConfirm(cropped, presetId);
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-3"
      style={{ background: 'rgba(0,0,0,0.72)', paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
      role="dialog"
      aria-modal
      aria-labelledby="admin-crop-title"
    >
      <div
        className="w-full max-w-md rounded-2xl overflow-hidden"
        style={{
          background: 'rgba(10,25,47,0.98)',
          border: '1px solid rgba(99,179,237,0.28)',
          boxShadow: '0 24px 48px rgba(0,0,0,0.45)',
        }}
      >
        <div className="px-4 pt-4 pb-2 border-b border-white/8">
          <p id="admin-crop-title" className="text-sm font-bold" style={{ color: 'var(--text-1)' }}>
            {title}
          </p>
          <p className="text-[10px] mt-1" style={{ color: 'var(--text-3)' }}>
            Формат · щипок — масштаб · перетаскивание — кадр. Ниже — превью для клиента.
          </p>
        </div>

        <div className="px-4 py-3 space-y-3">
          <div className="flex gap-1.5 flex-wrap">
            {presets.map((p) => {
              const on = presetId === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  disabled={saving}
                  onClick={() => setPresetId(p.id)}
                  className="press flex-1 min-w-[5.5rem] px-2 py-2 rounded-xl text-left disabled:opacity-40"
                  style={
                    on
                      ? { background: 'rgba(99,179,237,0.2)', border: '1px solid rgba(99,179,237,0.45)', color: '#93D7FF' }
                      : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-3)' }
                  }
                >
                  <span className="block text-[11px] font-bold">{p.label}</span>
                  <span className="block text-[9px] opacity-80">{p.hint}</span>
                </button>
              );
            })}
          </div>

          <div>
            <p className="text-[9px] font-bold uppercase tracking-wider mb-1.5" style={{ color: 'var(--text-3)' }}>
              Кадр
            </p>
            <AdminCropGestureViewport
              transform={transform}
              onTransformChange={(patch) => setTransform((t) => ({ ...t, ...patch }))}
              disabled={saving}
              aspectRatio={aspectCss}
            >
              <img src={source} alt="" className="w-full h-full object-cover pointer-events-none" style={imgStyle} />
            </AdminCropGestureViewport>
          </div>

          <div>
            <p className="text-[9px] font-bold uppercase tracking-wider mb-1.5" style={{ color: 'var(--text-3)' }}>
              Как на витрине
            </p>
            <CropContextPreview
              presetId={presetId}
              source={source}
              transform={transform}
              naturalAspect={naturalAspect}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-[10px]" style={{ color: 'var(--text-2)' }}>
              Масштаб · {transform.zoom.toFixed(2)}×
              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={transform.zoom}
                disabled={saving}
                onChange={(e) => setTransform((t) => ({ ...t, zoom: Number(e.target.value) }))}
                className="w-full mt-1"
              />
            </label>
            <label className="block text-[10px]" style={{ color: 'var(--text-2)' }}>
              Горизонталь
              <input
                type="range"
                min={-100}
                max={100}
                step={1}
                value={transform.panX}
                disabled={saving}
                onChange={(e) => setTransform((t) => ({ ...t, panX: Number(e.target.value) }))}
                className="w-full mt-1"
              />
            </label>
            <label className="block text-[10px]" style={{ color: 'var(--text-2)' }}>
              Вертикаль
              <input
                type="range"
                min={-100}
                max={100}
                step={1}
                value={transform.panY}
                disabled={saving}
                onChange={(e) => setTransform((t) => ({ ...t, panY: Number(e.target.value) }))}
                className="w-full mt-1"
              />
            </label>
          </div>
        </div>

        <div className="px-4 pb-4 flex flex-col gap-2">
          <button
            type="button"
            disabled={saving}
            onClick={() => void handleSave()}
            className="press w-full py-3 rounded-xl text-sm font-bold disabled:opacity-50"
            style={{ background: 'rgba(72,187,120,0.22)', color: '#68D391', border: '1px solid rgba(72,187,120,0.45)' }}
          >
            {saving ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Сохраняем…
              </span>
            ) : (
              'Сохранить фото'
            )}
          </button>
          {onConfirmRaw && (
            <button
              type="button"
              disabled={saving}
              className="press w-full py-2 rounded-xl text-[11px] font-semibold disabled:opacity-40"
              style={{ color: 'var(--text-3)', border: '1px dashed rgba(255,255,255,0.15)' }}
              onClick={async () => {
                const res = await fetch(source);
                const blob = await res.blob();
                const raw = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
                await onConfirmRaw(raw);
              }}
            >
              Исходный файл (без кадрирования)
            </button>
          )}
          <button
            type="button"
            disabled={saving}
            onClick={onCancel}
            className="press w-full py-2 rounded-xl text-xs font-semibold disabled:opacity-40"
            style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--text-2)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}

function CropContextPreview({
  presetId,
  source,
  transform,
  naturalAspect,
}: {
  presetId: AdminCropPresetId;
  source: string;
  transform: AdminCropTransform;
  naturalAspect: number;
}) {
  const preset = ADMIN_CROP_PRESETS[presetId];
  const cropAspect = resolveCropAspect(preset, Math.round(naturalAspect * 1000), 1000);
  const aspectCss = `${cropAspect} / 1`;
  const imgStyle = {
    transform: `translate(${transform.panX}%, ${transform.panY}%) scale(${transform.zoom})`,
    transformOrigin: 'center center' as const,
  };

  if (presetId === 'full_size') {
    return (
      <div
        className="w-full rounded-xl overflow-hidden max-h-36 relative"
        style={{ aspectRatio: aspectCss, border: '1px solid rgba(255,255,255,0.1)' }}
      >
        <img src={source} alt="" className="w-full h-full object-cover" style={imgStyle} />
        <span className="absolute bottom-1 right-1 text-[8px] px-1.5 py-0.5 rounded-full bg-black/50 text-white/80">
          полный кадр
        </span>
      </div>
    );
  }

  if (presetId === 'vitrine_tile') {
    return (
      <div
        className="mx-auto max-w-[9rem] rounded-2xl overflow-hidden h-28 relative"
        style={{ border: '1px solid var(--border-glass)', boxShadow: '0 8px 20px rgba(0,0,0,0.35)' }}
      >
        <div className="absolute inset-0" style={{ aspectRatio: aspectCss }}>
          <img src={source} alt="" className="w-full h-full object-cover" style={imgStyle} />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
        <p className="absolute bottom-2 left-2 right-2 text-[10px] font-bold text-white">Прямой</p>
      </div>
    );
  }

  if (presetId === 'square') {
    return (
      <div className="flex items-center gap-2">
        <div
          className="w-16 h-16 rounded-xl overflow-hidden shrink-0"
          style={{ border: '1px solid rgba(255,255,255,0.12)' }}
        >
          <img src={source} alt="" className="w-full h-full object-cover" style={imgStyle} />
        </div>
        <p className="text-[10px]" style={{ color: 'var(--text-3)' }}>
          Миниатюра в списке систем и SKU
        </p>
      </div>
    );
  }

  return (
    <div
      className="w-full rounded-xl overflow-hidden h-20 relative"
      style={{ border: '1px solid rgba(255,255,255,0.1)' }}
    >
      <img src={source} alt="" className="w-full h-full object-cover" style={imgStyle} />
      <span className="absolute bottom-1 right-1 text-[8px] px-1.5 py-0.5 rounded-full bg-black/50 text-white/80">
        16:9
      </span>
    </div>
  );
}
